import { MagicSearchPlugin, ResultItem } from './plugins';
import { createStore } from 'zustand';
import produce from 'immer';
import { SearchScope } from './plugins/scopeSearchPlugin';

const DEFAULT_INITIAL_STATE = {
  searchText: '',
  searchScopes: [],
};

export type SearchState = {
  searchText: string;
  searchScopes: SearchScope[];
};

const DEFAULT_SEARCH_STATE: Omit<
  SearchStoreState,
  | 'getFilteredPlugins'
  | 'getFilteredResults'
  | 'onResetStore'
  | 'onUpdateState'
  | 'onUpdateSearchScope'
  | 'onRemoveSearchScope'
  | 'onSaveCurrentSearch'
  | 'searchResults'
> = {
  initialState: DEFAULT_INITIAL_STATE,
  state: DEFAULT_INITIAL_STATE,
  focusedListItemIndex: -1,
  results: [],
  isOpen: false,
};

export type SearchStoreState = {
  // State if the user values that can be changed, text, scopes, etc.
  initialState: SearchState;
  state: SearchState;
  onUpdateState: (state: Partial<SearchState>) => void;
  onResetStore: () => void;
  onUpdateSearchScope: (searchScope: SearchScope) => void;
  onRemoveSearchScope: (searchScopeId: string) => void;
  focusedListItemIndex: number;
  results: ResultItem[];
  searchResults: (state: SearchState, plugins: MagicSearchPlugin[]) => Promise<void>;
  isOpen: boolean;
};

export type CreateSearchStoreParams = {
  initialState?: Partial<SearchState>;
};

export const createSearchStore = ({ initialState }: CreateSearchStoreParams) => {
  return createStore<SearchStoreState>()((set, get) => ({
    ...DEFAULT_SEARCH_STATE,
    state: {
      ...DEFAULT_SEARCH_STATE.state,
      ...initialState,
    },
    searchResults: async (state, plugins) => {
      const searchText = get().state.searchText;
      const searchResults: ResultItem[] = [];
      const pendingResults: { loadings: ResultItem[]; results: Promise<ResultItem[]> }[] = [];
      for (const plugin of plugins) {
        if ('onSearching' in plugin) {
          const loadings = plugin.onSearching(state);
          if (loadings.length) {
            pendingResults.push({
              loadings,
              results: plugin.onSearch(state),
            });
            searchResults.push(...loadings);
          }
        } else {
          searchResults.push(...plugin.onSearch(state));
        }
      }
      set(
        produce(get(), draftState => {
          draftState.results = searchResults;
        }),
      );
      for (const pendingResult of pendingResults) {
        const results = await pendingResult.results;

        // This works like a lock, if the search text has changed, we don't update the results
        // because they don't correspond to the current search
        if (get().state.searchText === searchText) {
          set(
            produce(get(), draftState => {
              const index = draftState.results.findIndex(
                ({ id }) => pendingResult.loadings[0].id === id,
              );
              draftState.results.splice(index, pendingResult.loadings.length, ...results);
            }),
          );
        }
      }
    },
    onResetStore: () =>
      set(
        produce(get(), draftState => {
          draftState.state = {
            ...DEFAULT_SEARCH_STATE.state,
            ...initialState,
          };
          draftState.focusedListItemIndex = -1;
          draftState.isOpen = false;
        }),
      ),
    onUpdateState: state =>
      set(
        produce(get(), draftState => {
          draftState.state = {
            ...draftState.state,
            ...state,
          };
        }),
      ),
    onUpdateSearchScope: searchScope => {
      const currentState = get();
      set(
        produce(currentState, draftState => {
          const index = draftState.state.searchScopes.findIndex(
            currentSearchScope => currentSearchScope.id === searchScope.id,
          );
          if (index !== -1) {
            draftState.state.searchScopes[index] = {
              ...draftState.state.searchScopes[index],
              ...searchScope,
            };
            draftState.state.searchText = '';
          } else {
            draftState.state.searchScopes.push(searchScope);
            draftState.state.searchText = '';
          }
        }),
      );
    },
    onRemoveSearchScope: searchScopeId =>
      set(
        produce(get(), draftState => {
          const index = draftState.state.searchScopes.findIndex(
            searchScope => searchScope.id === searchScopeId,
          );
          if (index !== -1) {
            draftState.state.searchScopes.splice(index, 1);
          }
        }),
      ),
  }));
};

export type SearchStore = ReturnType<typeof createSearchStore>;
